import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { HOTSPOTS, COLORS } from "@/components/zone/constants";

// Real content for The Academic Boardroom — written, not lorem.
const LANDMARK_DETAIL = {
  boardroom: {
    title: "Boardroom Hall",
    tagline: "Where the cohort meets, weekly, to think out loud — together.",
    paragraphs: [
      "Members of The Academic Boardroom convene here every Thursday at dusk. The Hall seats forty-eight around a single circular table — a deliberate constraint that forbids both the lectern and the back row. Every voice is equidistant from every other.",
      "The Boardroom’s only standing rule was set in 1908 by its first chair, Olwen Asher: “We come here to be changed by what we hear, not to defend what we came in believing.” Members read it aloud at the opening of each session.",
      "Topics are submitted anonymously the week prior. The Hall has hosted debates on Riemannian geometry, on the ethics of municipal road-naming, on whether a poem can be wrong. Minutes are kept; conclusions are not.",
    ],
    sessions: [
      { when: "Thursday · 19:00", what: "Open colloquy (members)" },
      { when: "First Sunday · 11:00", what: "Visitors’ session" },
    ],
  },
  library: {
    title: "Great Library",
    tagline: "Twelve thousand volumes. No fiction. No second editions when a first is available.",
    paragraphs: [
      "The Library’s collection is curated, not accumulated. Each acquisition is debated in open session and approved by simple majority — a process that takes, on average, eleven weeks per volume. The cataloguers consider this the appropriate amount of time.",
      "Members may borrow up to three titles at once, returnable on the next solstice. The catalogue is hand-typed, indexed twice (by subject and by argument), and accessible only from the Library itself.",
      "The reading room is silent by convention rather than by rule. The single concession to modernity is the brass desk lamp introduced in 1962, which members are quietly grateful for and never mention out loud.",
    ],
    sessions: [
      { when: "Mon–Sat · 09:00–22:00", what: "Open to members" },
      { when: "Tuesday · 16:00", what: "Acquisitions debate" },
    ],
  },
  lecture: {
    title: "Lecture Hall",
    tagline: "Tuesday evenings. One member, one hour, one argument that is not yet finished.",
    paragraphs: [
      "The Lecture Hall hosts the cohort’s weekly work-in-progress session: a member presents an argument they are still wrestling with, and the room helps them wrestle. Polished talks are politely declined.",
      "The slate is wiped, by hand, before each session. The benches descend in five tiers — the highest reserved for first-year members, who are expected to listen more than they speak. The lowest, by tradition, is left empty.",
      "Notable presentations enter the cohort’s running notebook. Notable mistakes enter it as well; the Boardroom holds that both are equally instructive, and that the latter age better.",
    ],
    sessions: [
      { when: "Tuesday · 18:30", what: "Work-in-progress" },
      { when: "Last Friday · 17:00", what: "Visiting fellow" },
    ],
  },
  bell: {
    title: "Bell Tower",
    tagline: "Cast in 1908. Rehung in 1962. Rung every hour since.",
    paragraphs: [
      "The Bell was cast by the Whitechapel foundry in the spring of 1908 — a gift from the founding cohort, paid for by subscription. The inscription on its waist reads, simply, AUDI ALTERAM PARTEM: hear the other side.",
      "After a hairline crack in the crown was discovered in 1961, the Bell was lowered, rehung on a new yoke, and returned to the tower the following May. It has rung on every hour since, with one intentional silence each January in memory of departed members.",
      "The Bell is the most punctual member of the Boardroom and, arguably, the most honest: it has nothing to defend, and yet always something to say.",
    ],
    sessions: [
      { when: "Every hour · 00–24", what: "Hourly toll" },
      { when: "1 January · silent", what: "In memoriam" },
    ],
  },
};

export default function LandmarkPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const data = LANDMARK_DETAIL[id];
  const hotspot = HOTSPOTS.find((h) => h.id === id);

  if (!data || !hotspot) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      data-testid="landmark-page"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(ellipse at top, #0f1f4a 0%, #0a1330 60%, #050a1c 100%)",
        color: COLORS.ivory,
        fontFamily: "Georgia, 'Times New Roman', serif",
        padding: "64px 8vw",
        boxSizing: "border-box",
      }}
    >
      <button
        data-testid="landmark-back"
        onClick={() => navigate("/")}
        style={{
          background: "transparent",
          border: `1px solid ${COLORS.navyMid}`,
          color: COLORS.gold,
          padding: "10px 18px",
          borderRadius: 999,
          cursor: "pointer",
          fontFamily: "Georgia, serif",
          fontSize: 12,
          letterSpacing: 2.5,
          textTransform: "uppercase",
          marginBottom: 40,
        }}
      >
        ← Back to the Quad
      </button>

      <div
        data-testid="landmark-eyebrow"
        style={{
          fontSize: 11,
          letterSpacing: 5,
          color: COLORS.gold,
          textTransform: "uppercase",
          marginBottom: 14,
        }}
      >
        LANDMARK · {id}
      </div>

      <h1
        data-testid="landmark-title"
        style={{
          fontSize: "clamp(40px, 6vw, 80px)",
          margin: 0,
          color: COLORS.goldBright,
          letterSpacing: 0.5,
          lineHeight: 1.05,
        }}
      >
        {data.title}
      </h1>

      <div
        style={{
          marginTop: 18,
          fontSize: 20,
          fontStyle: "italic",
          color: COLORS.ivory,
          opacity: 0.85,
          maxWidth: 720,
        }}
      >
        {data.tagline}
      </div>

      <div
        style={{
          width: 80,
          height: 2,
          background: COLORS.gold,
          marginTop: 28,
          marginBottom: 28,
        }}
      />

      <div style={{ maxWidth: 720, lineHeight: 1.8, fontSize: 17, opacity: 0.92 }}>
        {data.paragraphs.map((p, i) => (
          <p
            key={i}
            data-testid={`landmark-paragraph-${i}`}
            style={{ marginBottom: 18 }}
          >
            {p}
          </p>
        ))}
      </div>

      {/* Sessions / schedule */}
      <div
        data-testid="landmark-sessions"
        style={{
          marginTop: 36,
          maxWidth: 720,
          padding: "20px 24px",
          background: "rgba(11,21,48,0.55)",
          border: `1px solid ${COLORS.navyMid}`,
          borderRadius: 14,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 4,
            color: COLORS.gold,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Sessions
        </div>
        {data.sessions.map((s, i) => (
          <div
            key={i}
            data-testid={`landmark-session-${i}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              padding: "10px 0",
              borderBottom:
                i < data.sessions.length - 1 ? `1px solid ${COLORS.navyMid}` : "none",
              fontSize: 15,
            }}
          >
            <div style={{ color: COLORS.goldBright, letterSpacing: 1 }}>{s.when}</div>
            <div style={{ color: COLORS.ivory, opacity: 0.85 }}>{s.what}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 36,
          padding: "20px 24px",
          background: "rgba(11,21,48,0.55)",
          border: `1px solid ${COLORS.navyMid}`,
          borderRadius: 14,
          maxWidth: 720,
          color: COLORS.ivory,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 4,
            color: COLORS.gold,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Continue exploring
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {HOTSPOTS.filter((h) => h.id !== id).map((h) => (
            <button
              key={h.id}
              data-testid={`landmark-link-${h.id}`}
              onClick={() => navigate(h.route)}
              style={{
                background: "transparent",
                border: `1px solid ${COLORS.gold}`,
                color: COLORS.gold,
                padding: "8px 16px",
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "Georgia, serif",
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: "uppercase",
              }}
            >
              {h.label} →
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
